import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  AdminAuthProvider,
  useAdminAuth,
  formatApiErrorDetail,
} from "../../lib/admin-auth-context";
import { LOGO_URL } from "../../lib/i18n";
import SEO from "../../components/SEO";
import { Eye, EyeSlash, ArrowRight, ShieldStar } from "@phosphor-icons/react";

const Form = () => {
  const { user, login } = useAdminAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user === undefined) return null; // loading
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Sesión iniciada");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <SEO title="Acceso interno · ICOR" noIndex />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(50% 60% at 50% 0%, rgba(0,82,255,0.18), transparent 60%)",
        }}
        aria-hidden
      />

      <a href="/" className="relative mb-8">
        <img
          src={LOGO_URL}
          alt="ICOR"
          className="h-20 w-auto"
          style={{ mixBlendMode: "screen", filter: "brightness(2.6) contrast(1.1)" }}
        />
      </a>

      <div
        className="relative w-full max-w-md bg-white text-slate-900 p-8 md:p-10 border border-white/10"
        style={{ borderRadius: 4 }}
      >
        <div className="flex items-center gap-2 text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
          <ShieldStar size={14} weight="bold" />
          Portal Interno
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
          Acceso para empleados
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Ingresa con tu cuenta corporativa ICOR.
        </p>

        <form onSubmit={submit} className="mt-8" data-testid="admin-login-form">
          <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
            Correo
          </label>
          <input
            type="email"
            required
            data-testid="admin-login-email"
            className="icor-input mb-5"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@icor.com.mx"
            autoFocus
          />

          <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
            Contraseña
          </label>
          <div className="relative mb-5">
            <input
              type={showPass ? "text" : "password"}
              required
              data-testid="admin-login-password"
              className="icor-input pr-12"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="toggle"
            >
              {showPass ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-5 p-3 text-sm text-red-700 bg-red-50 border border-red-200"
              style={{ borderRadius: 4 }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-primary w-full disabled:opacity-60"
          >
            {submitting ? "Entrando..." : "Iniciar sesión"}
            <ArrowRight size={16} />
          </button>
        </form>
      </div>

      <a
        href="/"
        className="relative mt-6 text-xs tracking-[0.18em] uppercase text-white/50 hover:text-white"
      >
        ← Volver al sitio público
      </a>
    </div>
  );
};

export default function AdminLogin() {
  return (
    <AdminAuthProvider>
      <Form />
      <Toaster position="top-right" richColors />
    </AdminAuthProvider>
  );
}
